import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../config';
import {
  ArrowLeft,
  FileText,
  Truck,
  ShieldCheck,
  TestTube,
  ClipboardList,
  AlertCircle,
  CheckCircle2,
  Filter,
} from 'lucide-react';
import '../../App.css';

const CATEGORIES = [
  { key: 'driverQualification', label: 'Driver Qualification File', icon: FileText, route: '/documents' },
  { key: 'vehicleMaintenance', label: 'Vehicle Maintenance File', icon: Truck, route: '/vehicles' },
  { key: 'drugAlcohol', label: 'Drug & Alcohol', icon: TestTube, route: '/clearinghouse' },
  { key: 'insuranceAuthority', label: 'Insurance & Authority', icon: ShieldCheck, route: '/authority' },
  { key: 'accidentRegister', label: 'Accident Register', icon: ClipboardList, route: '/documents' },
];

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'missing', label: 'Missing' },
  { value: 'due_soon', label: 'Due soon' },
  { value: 'ok', label: 'OK' },
];

const statusColors = { READY: '#10B981', AT_RISK: '#F59E0B', NOT_READY: '#EF4444' };

export default function AuditChecklistPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categoryFilter = searchParams.get('category') || 'all';
  const statusFilter = searchParams.get('status') || 'all';

  const fetchReadiness = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/audit/readiness`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      const url = `${API_URL || '(same origin)'}/api/audit/readiness`;
      console.error('Audit readiness error:', err?.response?.status, url, err);
      if (err.response?.status === 404) {
        setError('Audit API not found (404). Restart the backend (npm run start:dev) and ensure it runs on port 5001.');
      } else if (err.response?.status === 401) {
        setError('Please log in again.');
      } else if (err.response?.status === 429) {
        setError('Too many requests. Wait a moment and refresh, or restart the backend if the rate limit is very low.');
      } else {
        setError(err.response?.data?.message || 'Failed to load audit checklist.');
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReadiness();
  }, []);

  const filteredCategories = useMemo(() => {
    if (categoryFilter === 'all') return CATEGORIES;
    const c = CATEGORIES.find((cat) => cat.key === categoryFilter);
    return c ? [c] : CATEGORIES;
  }, [categoryFilter]);

  const getCategoryChecklist = (catKey) => {
    if (!data?.byCategory?.[catKey]) return { missing: [], dueSoon: [], score: 0 };
    const c = data.byCategory[catKey];
    const missing = (c.missing || []).map((item) => ({ item, status: 'missing' }));
    const dueSoon = (c.dueSoon || []).map((item) => ({ item, status: 'due_soon' }));
    return { missing, dueSoon, score: c.score ?? 0 };
  };

  const passesStatusFilter = (status) => {
    if (statusFilter === 'all') return true;
    return statusFilter === status;
  };

  if (loading) {
    return (
      <div className="audit-checklist-page">
        <div className="audit-checklist-loading">
          <div className="loading-spinner" />
          <p>Loading audit checklist...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="audit-checklist-page">
        <div className="audit-checklist-header">
          <button type="button" className="button secondary back-button" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
          <h1>DOT Audit Readiness Checklist</h1>
        </div>
        <div className="audit-checklist-error">
          <p>{error}</p>
          <button type="button" className="button" onClick={fetchReadiness}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasData = data && (data.missingItems?.length > 0 || data.dueSoonItems?.length > 0 || data.byCategory);

  return (
    <div className="audit-checklist-page">
      <div className="audit-checklist-header">
        <button type="button" className="button secondary back-button" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={20} />
          Back to Dashboard
        </button>
        <h1>DOT Audit Readiness Checklist</h1>
        {data && (
          <div className="audit-checklist-summary">
            <div className="audit-summary-score" style={{ borderColor: statusColors[data.status] }}>
              <span className="score-value" style={{ color: statusColors[data.status] }}>{data.readinessScore}</span>
              <span className="score-max">/100</span>
            </div>
            <span className="audit-summary-status" style={{ color: statusColors[data.status] }}>
              {data.status?.replace('_', ' ') || '—'}
            </span>
          </div>
        )}
      </div>

      {data && (
        <div className="audit-checklist-filters">
          <span className="filter-label"><Filter size={16} /> Filter</span>
          <select
            value={categoryFilter}
            onChange={(e) => setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set('category', e.target.value); return p; })}
            className="audit-filter-select"
            aria-label="Filter by category"
          >
            <option value="all">All categories</option>
            {CATEGORIES.map((cat) => (
              <option key={cat.key} value={cat.key}>{cat.label}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set('status', e.target.value); return p; })}
            className="audit-filter-select"
            aria-label="Filter by status"
          >
            {STATUS_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
      )}

      {data && (
        <div className="audit-checklist-sections">
          {filteredCategories.map((cat) => {
            const { missing, dueSoon, score } = getCategoryChecklist(cat.key);
            const Icon = cat.icon;
            const hasMissing = missing.length > 0;
            const hasDueSoon = dueSoon.length > 0;
            const hasOk = (hasMissing || hasDueSoon) ? false : true;

            const showSection =
              statusFilter === 'all' ||
              (statusFilter === 'missing' && hasMissing) ||
              (statusFilter === 'due_soon' && hasDueSoon) ||
              (statusFilter === 'ok' && hasOk);

            if (!showSection) return null;

            return (
              <section key={cat.key} className="audit-checklist-section category-section">
                <div className="audit-category-header">
                  <Icon size={22} />
                  <h2>{cat.label}</h2>
                  <span className="audit-category-score" style={{ color: score >= 18 ? '#10B981' : score >= 12 ? '#F59E0B' : '#EF4444' }}>
                    {score}/20
                  </span>
                  <button type="button" className="button small" onClick={() => navigate(cat.route)}>
                    Go to {cat.key === 'driverQualification' ? 'Documents' : cat.key === 'vehicleMaintenance' ? 'Vehicles' : cat.key === 'drugAlcohol' ? 'Clearinghouse' : cat.key === 'insuranceAuthority' ? 'Authority' : 'Documents'}
                  </button>
                </div>
                <ul className="audit-checklist-list">
                  {hasMissing && passesStatusFilter('missing') && missing.map((m, idx) => (
                    <li key={`m-${cat.key}-${idx}`} className="audit-checklist-item missing">
                      <AlertCircle size={18} color="#EF4444" />
                      <div>
                        <strong>{m.item}</strong>
                        <span className="audit-category">{cat.label}</span>
                      </div>
                      <button type="button" className="button small" onClick={() => navigate(cat.route)}>Fix</button>
                    </li>
                  ))}
                  {hasDueSoon && passesStatusFilter('due_soon') && dueSoon.map((m, idx) => (
                    <li key={`d-${cat.key}-${idx}`} className="audit-checklist-item due-soon">
                      <AlertCircle size={18} color="#F59E0B" />
                      <div>
                        <strong>{m.item}</strong>
                        <span className="audit-category">{cat.label}</span>
                      </div>
                      <button type="button" className="button small secondary" onClick={() => navigate(cat.route)}>Review</button>
                    </li>
                  ))}
                  {statusFilter === 'ok' && hasOk && (
                    <li className="audit-checklist-item ok">
                      <CheckCircle2 size={18} color="#10B981" />
                      <div>
                        <strong>Requirements met for this category</strong>
                        <span className="audit-category">{cat.label}</span>
                      </div>
                    </li>
                  )}
                  {statusFilter === 'all' && hasOk && !hasMissing && !hasDueSoon && (
                    <li className="audit-checklist-item ok">
                      <CheckCircle2 size={18} color="#10B981" />
                      <div>
                        <strong>Requirements met for this category</strong>
                        <span className="audit-category">{cat.label}</span>
                      </div>
                    </li>
                  )}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      {data && data.recommendations?.length > 0 && (
        <section className="audit-checklist-section">
          <h2>Recommendations</h2>
          <ul className="audit-recommendations-list">
            {data.recommendations.map((rec, idx) => (
              <li key={idx}>{rec}</li>
            ))}
          </ul>
        </section>
      )}

      {data && !hasData && (
        <div className="audit-checklist-empty">
          <ShieldCheck size={48} color="#10B981" />
          <p>No audit data yet. Complete your profile and documents to see your checklist.</p>
          <button type="button" className="button" onClick={() => navigate('/documents')}>Go to Documents</button>
        </div>
      )}

      {data && statusFilter !== 'all' && statusFilter === 'missing' && !data.missingItems?.length && (
        <div className="audit-checklist-empty">
          <CheckCircle2 size={48} color="#10B981" />
          <p>No missing items. You're in good shape for this category.</p>
          <button type="button" className="button secondary" onClick={() => setSearchParams((p) => { const next = new URLSearchParams(p); next.set('status', 'all'); return next; })}>Show all</button>
        </div>
      )}
      {data && statusFilter !== 'all' && statusFilter === 'due_soon' && !data.dueSoonItems?.length && (
        <div className="audit-checklist-empty">
          <CheckCircle2 size={48} color="#10B981" />
          <p>Nothing due soon. Keep it up.</p>
          <button type="button" className="button secondary" onClick={() => setSearchParams((p) => { const next = new URLSearchParams(p); next.set('status', 'all'); return next; })}>Show all</button>
        </div>
      )}
    </div>
  );
}
